import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";

interface SpeechRecognitionContextType {
  transcript: string;
  isListening: boolean;
  startListening: (lang: string) => void;
  stopListening: () => void;
  resetTranscript: () => void;
  browserSupportsSpeechRecognition: boolean;
  activeListenerId: string | null;
  setActiveListenerId: (id: string | null) => void;
}

const SpeechRecognitionContext = createContext<
  SpeechRecognitionContextType | undefined
>(undefined);

export const SpeechRecognitionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const [activeListenerId, setActiveListenerId] = useState<string | null>(null);

  const startListening = useCallback(
    async (lang: string) => {
      resetTranscript();
      await SpeechRecognition.startListening({
        continuous: false,
        language: lang,
      });
    },
    [resetTranscript]
  );

  const stopListening = useCallback(() => {
    SpeechRecognition.stopListening();
  }, []);

  const value = {
    transcript,
    isListening: listening,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    activeListenerId,
    setActiveListenerId,
  };

  return (
    <SpeechRecognitionContext.Provider value={value}>
      {children}
    </SpeechRecognitionContext.Provider>
  );
};

export const useSpeechRecognitionManager = (): SpeechRecognitionContextType => {
  const context = useContext(SpeechRecognitionContext);
  if (context === undefined) {
    throw new Error(
      "useSpeechRecognitionManager must be used within a SpeechRecognitionProvider"
    );
  }
  return context;
};
